import { Component, Output, EventEmitter, Input, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CreditEngineServices } from '../../loan-applications/credit-engine.service';
import { UserLoginService } from 'src/app/login/userlogin.service';
import { CreditBureauSummaryReadByUserIdResponse, CreditBureauSummaryItem, CreditBureauReportItem } from '../../loan-applications/credit-engine.interface';
import { ApplicationDetailsDTO } from '../../loan-applications/loan-application-details/loan-application-details.interface';
import { LoanApplicationService } from '../../loan-applications/loan-applications.service';
import { LoanApplicationUpdateDTO, LoanApplicationItem } from '../../loan-applications/loan-applications.interface';
import { ToastrService } from 'ngx-toastr';
import { LosStateService } from '../los-state.service';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-bureau-aa',
  templateUrl: './bureau-aa.component.html',
  styleUrls: ['./bureau-aa.component.css']
})
export class BureauAaComponent implements OnInit {
  @Input() applicationId: number | null = null;
  @Output() next = new EventEmitter<void>();
  @Output() prev = new EventEmitter<void>();

  private creditEngineService    = inject(CreditEngineServices);
  private loanApplicationService = inject(LoanApplicationService);
  private userLoginService       = inject(UserLoginService);
  private losState               = inject(LosStateService);
  private toastr                 = inject(ToastrService);
  private destroyRef             = inject(DestroyRef);

  // ─── State ────────────────────────────────────────────────────────────────
  applicationData:    LoanApplicationItem | null                     = null;
  bureauSummary:      CreditBureauSummaryReadByUserIdResponse | null = null;
  latestSummary:      CreditBureauSummaryItem | null                 = null;
  latestReport:       CreditBureauReportItem | null                  = null;
  reportJson:         any                                            = null;
  accountsList:       any[]                                          = [];
  applicationDetails: ApplicationDetailsDTO[]                        = [];
  isLoading = false;

  // ─── Lifecycle ────────────────────────────────────────────────────────────
  ngOnInit(): void {
    this.losState.applicationData$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(app => {
        this.applicationData = app;
        if (app?.userId) {
          this.fetchBureauSummary(app.userId);
          this.fetchBureauReport(app.userId);
        }
      });

    this.losState.applicationDetails$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(details => {
        if (details) this.applicationDetails = details;
      });
  }

  // ─── Computed helpers ─────────────────────────────────────────────────────

  /** True when at least one BS parameter has been persisted in applicationDetails */
  get hasBankStatementData(): boolean {
    return this.applicationDetails.some(d => d.parameterName?.startsWith('BS ') && !!d.value);
  }

  private get actionUser(): string {
    return this.userLoginService.User()?.userId?.toString() || '0';
  }

  // ─── Bureau fetch ─────────────────────────────────────────────────────────
  fetchBureauSummary(userId: number): void {
    this.isLoading = true;
    this.creditEngineService.CreditBureauSummary_ReadByUserId({ userId }).subscribe({
      next: (res) => {
        if (res?.items?.length) {
          this.bureauSummary = res;
          this.latestSummary = res.items[res.items.length - 1];
        }
        this.isLoading = false;
      },
      error: (err) => {
        this.toastr.error('Failed to fetch bureau summary data');
        console.error('Bureau summary error:', err);
        this.isLoading = false;
      }
    });
  }

  fetchBureauReport(userId: number): void {
    this.creditEngineService.CreditBureauReport_ReadByUserId({ userId }).subscribe({
      next: (res) => {
        if (res?.items?.length) {
          this.latestReport = res.items[res.items.length - 1];
          if (this.latestReport?.rawReportJSON) {
            try {
              const parsed = typeof this.latestReport.rawReportJSON === 'string'
                ? JSON.parse(this.latestReport.rawReportJSON)
                : this.latestReport.rawReportJSON;
              this.reportJson  = parsed.EquifaxRetail || parsed;
              this.accountsList = this.getAccountsList();
            } catch (e) {
              console.error('Error parsing rawReportJSON:', e);
            }
          }
        }
      },
      error: (err) => console.error('Error fetching bureau report:', err)
    });
  }

  // ─── Accounts list builder ────────────────────────────────────────────────
  getAccountsList(): any[] {
    const list: any[] = [];
    if (!this.reportJson?.accountSummary) return list;

    const active = this.reportJson.accountSummary.activeAccounts || {};
    const closed = this.reportJson.accountSummary.closedAccounts || {};

    const toReadable = (type: string) =>
      type.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim();

    for (const key of Object.keys(active)) {
      if (Array.isArray(active[key])) {
        active[key].forEach((acc: any) => list.push({
          lender:      acc.lender         || 'Unknown',
          type:        toReadable(key),
          outstanding: acc.currentBalance || '0',
          emi:         acc.emi            ?? null,
          dpd:         acc.countofdelays  || '0',
          status:      'Active',
          statusClass: 'b-g'
        }));
      }
    }

    for (const key of Object.keys(closed)) {
      if (Array.isArray(closed[key])) {
        closed[key].forEach((acc: any) => list.push({
          lender:      acc.lender         || 'Unknown',
          type:        toReadable(key),
          outstanding: acc.currentBalance || '0',
          emi:         acc.emi            ?? null,
          dpd:         acc.countofdelays  || '0',
          status:      'Closed',
          statusClass: 'b-gray'
        }));
      }
    }

    return list;
  }

  // ─── BS parameter accessor ────────────────────────────────────────────────
  /**
   * Read a persisted Bank-Statement parameter from applicationDetails.
   * Used in the HTML as: getBSValue('BS Avg Monthly Credit', '0')
   */
  getBSValue(parameterName: string, defaultValue: string = ''): string {
    if (!this.applicationDetails?.length) return defaultValue;
    const match = this.applicationDetails.find(
      d => d.parameterName?.toLowerCase() === parameterName.toLowerCase()
    );
    const val = match?.value;
    return (val != null && val !== '') ? val : defaultValue;
  }

  // ─── Fraud signal helper ──────────────────────────────────────────────────
  /**
   * Returns true when at least one fraud/risk flag is active.
   * Drives the "✓ No fraud signals detected" fallback tag in the template.
   */
  hasAnyFraudSignal(): boolean {
    return (
      this.getBSValue('BS Suspicious Dummy Name', '0')   === '1' ||
      this.getBSValue('BS Circular Txn Detected', '0')   === '1' ||
      this.getBSValue('BS UPI Circular Suspected', '0')  === '1' ||
      +this.getBSValue('BS Gambling Txn Count', '0')     > 0     ||
      +this.getBSValue('BS Crypto Txn Count', '0')       > 0     ||
      this.getBSValue('BS Artificial Balance Buildup', '0') === '1' ||
      +this.getBSValue('BS Frequent Loan App Txns', '0') > 0
    );
  }

  // ─── Score colour helper ──────────────────────────────────────────────────
  /**
   * Returns a CSS colour variable based on whether the score clears a threshold.
   * @param score     Numeric score to evaluate
   * @param threshold Minimum value considered "good"
   */
  getScoreColor(score: number, threshold: number): string {
    return score >= threshold ? 'var(--green)' : 'var(--rose)';
  }

  // ─── Proceed ──────────────────────────────────────────────────────────────
  async proceed(): Promise<void> {
    if (this.isLoading) return;
    this.isLoading = true;

    try {
      if (this.applicationData && this.applicationId) {
        const statusPriority: Record<string, number> = {
          'Draft':            0,
          'ConsentPending':   1,
          'KYCPending':       2,
          'KYCVerified':      3,
          'FinancialComplete': 4,
          'BureauComplete':   5
        };

        const currentPrio = statusPriority[this.applicationData.applicationStatus || 'Draft'] ?? 99;

        if (currentPrio <= 4) {
          const payload = this.buildAppUpdatePayload('BureauComplete');
          const updated = await firstValueFrom(this.loanApplicationService.LoanApplication_Update(payload));
          this.losState.patchApplication(updated);
        }
      }

      this.toastr.success('Bureau progress updated');
      this.next.emit();
    } catch (err) {
      this.toastr.error('Failed to update step progress');
      console.error('Bureau AA proceed error:', err);
    } finally {
      this.isLoading = false;
    }
  }

  // ─── Build loan application update payload ────────────────────────────────
  private buildAppUpdatePayload(status: string): LoanApplicationUpdateDTO {
    const a = this.applicationData!;
    return {
      applicationId:      a.applicationId,
      userId:             a.userId,
      productId:          a.productId,
      applicationStatus:  status,
      requestedAmount:    a.requestedAmount,
      approvedAmount:     a.approvedAmount,
      tenureMonths:       a.tenureMonths,
      applicationDate:    a.applicationDate,
      approvalDate:       a.approvalDate,
      approvedBy:         a.approvedBy         || '',
      rejectionReason:    a.rejectionReason    || '',
      rejectedBy:         a.rejectedBy         || '',
      isDisbursed:        a.isDisbursed,
      disbursedOn:        a.disbursedOn,
      disbursedAmount:    a.disbursedAmount,
      subUserId:          a.subUserId,
      businessId:         a.businessId,
      productCode:        a.productCode        || '',
      prrn:               a.prrn,
      lmsApplicationName: a.lmsApplicationName,
      otherDetail1:       a.otherDetail1,
      otherDetail2:       a.otherDetail2,
      otherDetail3:       a.otherDetail3,
      otherDetail4:       a.otherDetail4,
      otherDetail5:       a.otherDetail5,
      otherDetail6:       a.otherDetail6,
      isActive:           1,
      actionUser:         this.actionUser
    };
  }
}